import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Autocomplete,
  Avatar,
  Stack,
  alpha,
} from '@mui/material';
import { Send, Delete, Email, HelpOutline } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import EmailTabs from './EmailTabs';
import { useI18n } from '../../i18n/useI18n';

const EmailComposer: React.FC = () => {
  const { admin: a, pages: t, formatMessage } = useI18n();
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [emailList, setEmailList] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0 });
  const MAX_EMAILS = 99;

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ font: [] }], // dùng danh sách font mặc định (Sans Serif, Serif, Monospace)
      [{ size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'color',
    'background',
    'align',
    'link',
    'image',
  ];

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Parse emails từ text (hỗ trợ khoảng trắng, nhiều dòng, dấu phẩy, dấu chấm phẩy)
  const parseEmails = (text: string): string[] => {
    // Tách theo khoảng trắng, xuống dòng, dấu phẩy, dấu chấm phẩy
    return text
      .split(/[\s\n,;]+/)
      .map(email => email.trim())
      .filter(email => email && isValidEmail(email));
  };

  const getColorForEmail = (email: string): string => {
    const colors = [
      '#f44336',
      '#e91e63',
      '#9c27b0',
      '#3f51b5',
      '#2196f3',
      '#009688',
      '#4caf50',
      '#ff9800',
      '#795548',
      '#607d8b',
    ];
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = email.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  // Chữ trong avatar đổi màu theo độ sáng của nền để dễ đọc
  const getAvatarTextColor = (bgColor: string): string => {
    const hex = bgColor.replace('#', '');
    if (hex.length !== 6) return '#fff';
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.6 ? '#fff' : '#000';
  };

  // Xử lý khi thay đổi email list
  const handleEmailChange = (event: any, newValue: (string | string[])[]) => {
    // Filter và validate emails
    const validEmails = newValue
      .map(item => typeof item === 'string' ? item.trim() : '')
      .filter(email => email && isValidEmail(email));

    // Loại bỏ trùng lặp
    const uniqueEmails = Array.from(new Set(validEmails));

    // Giới hạn số lượng email
    if (uniqueEmails.length > MAX_EMAILS) {
      toast.warning(`Chỉ được thêm tối đa ${MAX_EMAILS} email`);
      setEmailList(uniqueEmails.slice(0, MAX_EMAILS));
    } else {
      setEmailList(uniqueEmails);
    }
  };

  // Xử lý khi nhập/paste email
  const handleInputChange = (event: any, value: string, reason: string) => {
    // Chỉ update inputValue, không parse tự động khi đang nhập
    setInputValue(value);
    return value;
  };

  // Xử lý khi nhấn phím (để detect Space hoặc Enter)
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Space' || event.key === 'Enter') {
      const currentValue = inputValue.trim();
      
      if (currentValue && isValidEmail(currentValue)) {
        event.preventDefault();
        // Thêm email vào list
        const uniqueEmails = Array.from(new Set([...emailList, currentValue]));
        if (uniqueEmails.length > MAX_EMAILS) {
          toast.warning(`Chỉ được thêm tối đa ${MAX_EMAILS} email`);
          setEmailList(uniqueEmails.slice(0, MAX_EMAILS));
        } else {
          setEmailList(uniqueEmails);
        }
        // Clear input
        setInputValue('');
      } else if (currentValue && !isValidEmail(currentValue)) {
        // Nếu không phải email hợp lệ, chỉ clear khi nhấn Space/Enter
        if (event.key === 'Enter') {
          event.preventDefault();
          toast.warning('Email không hợp lệ');
        }
      }
    }
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Vui lòng nhập tiêu đề email');
      return;
    }

    if (!htmlContent.trim()) {
      toast.error('Vui lòng nhập nội dung email');
      return;
    }

    if (emailList.length === 0) {
      toast.error('Vui lòng thêm ít nhất một email');
      return;
    }

    try {
      setSending(true);
      setSendProgress({ sent: 0, total: emailList.length });

      // Gửi bulk 1 request (backend sẽ gửi tuần tự và lưu theo campaign)
      const res = await api.sendAdminEmail({
        recipients: emailList,
        subject,
        html: htmlContent,
      });

      const sent = res.stats?.sent ?? 0;
      const total = res.stats?.total ?? emailList.length;
      const failed = res.stats?.failed ?? Math.max(total - sent, 0);
      setSendProgress({ sent, total });

      if (sent > 0) toast.success(`Đã gửi thành công ${sent}/${total} email`);
      if (failed > 0) toast.warning(`Không thể gửi ${failed} email`);

      // Reset form sau khi gửi thành công
      if (failed === 0) {
        setSubject('');
        setHtmlContent('');
        setEmailList([]);
        setInputValue('');
      }
    } catch (error: any) {
      console.error('Error sending emails:', error);
      toast.error(error.response?.data?.error || 'Có lỗi xảy ra khi gửi email');
    } finally {
      setSending(false);
      setSendProgress({ sent: 0, total: 0 });
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 1.5, sm: 3 },
        px: { xs: 1.5, sm: 3 },
        '@keyframes emSoftIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          mb: { xs: 1.5, sm: 2 },
          p: { xs: 1.75, sm: 2.5 },
          borderRadius: { xs: 3, sm: 4 },
          border: '1px solid',
          borderColor: alpha('#f48fb1', 0.25),
          background: `linear-gradient(135deg, ${alpha('#fff', 0.95)} 0%, ${alpha('#ffc1e3', 0.35)} 100%)`,
          boxShadow: `0 10px 28px ${alpha('#bf5f82', 0.1)}`,
          animation: 'emSoftIn 0.45s ease both',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha('#f48fb1', 0.2),
              color: 'primary.dark',
              flexShrink: 0,
            }}
          >
            <Email />
          </Box>
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.35rem', sm: '1.85rem' },
              letterSpacing: '-0.02em',
              color: '#2d2430',
            }}
          >
            {a.email.title}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ animation: 'emSoftIn 0.45s ease both', animationDelay: '0.05s' }}>
        <EmailTabs />
      </Box>

      <Grid container spacing={{ xs: 1.5, sm: 2.5 }}>
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: { xs: 3, sm: 4 },
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: `0 8px 22px ${alpha('#bf5f82', 0.06)}`,
              animation: 'emSoftIn 0.45s ease both',
              animationDelay: '0.1s',
            }}
          >
            <TextField
              fullWidth
              label={a.email.subject}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={a.email.subjectPlaceholder}
              size="small"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  bgcolor: alpha('#f48fb1', 0.04),
                },
              }}
            />

            <Typography
              sx={{
                mb: 1,
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                color: alpha('#2d2430', 0.7),
              }}
            >
              {a.email.bodyLabel}
            </Typography>
            <Box
              sx={{
                '& .ql-toolbar': {
                  borderRadius: '12px 12px 0 0',
                  flexWrap: 'wrap',
                  bgcolor: alpha('#f48fb1', 0.04),
                },
                '& .ql-container': {
                  borderRadius: '0 0 12px 12px',
                  minHeight: { xs: 200, sm: 320 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
                '& .quill': {
                  borderRadius: 3,
                  overflow: 'hidden',
                },
              }}
            >
              <ReactQuill
                theme="snow"
                value={htmlContent}
                onChange={setHtmlContent}
                modules={modules}
                formats={formats}
                placeholder={a.email.bodyPlaceholder}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: { xs: 3, sm: 4 },
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: `0 8px 22px ${alpha('#bf5f82', 0.06)}`,
              animation: 'emSoftIn 0.45s ease both',
              animationDelay: '0.14s',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                {a.email.recipientList}
              </Typography>
              <Tooltip title="Paste nhiều email từ Excel hoặc nhập từng email. Email sẽ tự động được tách và thêm vào danh sách.">
                <IconButton size="small" sx={{ color: 'primary.main' }}>
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Box sx={{ mb: 1.5, flexGrow: 1 }}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={emailList}
                inputValue={inputValue}
                onChange={handleEmailChange}
                onInputChange={handleInputChange}
                filterOptions={() => []}
                getOptionLabel={(option) => (typeof option === 'string' ? option : '')}
                isOptionEqualToValue={(option, value) => option === value}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const email = option as string;
                    const color = getColorForEmail(email);
                    const letter = email.charAt(0).toUpperCase() || '?';
                    return (
                      <Chip
                        variant="outlined"
                        label={email}
                        size="small"
                        avatar={
                          <Avatar
                            sx={{
                              bgcolor: color,
                              width: 22,
                              height: 22,
                              fontSize: 12,
                              color: getAvatarTextColor(color),
                              '&.MuiChip-avatarColorDefault': {
                                bgcolor: color,
                                color: getAvatarTextColor(color),
                              },
                            }}
                          >
                            {letter}
                          </Avatar>
                        }
                        {...getTagProps({ index })}
                        key={email}
                        sx={{ maxWidth: '100%' }}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={a.email.recipientPlaceholder}
                    variant="outlined"
                    fullWidth
                    size="small"
                    onKeyDown={handleKeyDown}
                    onPaste={(e) => {
                      const pastedText = e.clipboardData.getData('text');
                      if (pastedText) {
                        e.preventDefault();
                        const emails = parseEmails(pastedText);
                        if (emails.length > 0) {
                          const uniqueEmails = Array.from(new Set([...emailList, ...emails]));
                          if (uniqueEmails.length > MAX_EMAILS) {
                            toast.warning(`Chỉ được thêm tối đa ${MAX_EMAILS} email`);
                            setEmailList(uniqueEmails.slice(0, MAX_EMAILS));
                          } else {
                            setEmailList(uniqueEmails);
                          }
                          setInputValue('');
                        }
                      }
                    }}
                  />
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: { xs: 100, sm: 120 },
                    alignItems: 'flex-start',
                    paddingTop: '8px',
                    borderRadius: 2.5,
                    bgcolor: alpha('#f48fb1', 0.04),
                  },
                  '& .MuiAutocomplete-inputRoot': {
                    flexWrap: 'wrap',
                  },
                }}
              />
            </Box>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Chip
                size="small"
                label={`${emailList.length} / ${MAX_EMAILS}`}
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha('#f48fb1', 0.12),
                  color: 'primary.dark',
                }}
              />
              {emailList.length > 0 && (
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  startIcon={<Delete />}
                  onClick={() => {
                    if (window.confirm(a.email.clearAllConfirm)) {
                      setEmailList([]);
                    }
                  }}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  {a.email.clearAll}
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {sending && (
        <Alert severity="info" sx={{ mt: 2, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography>
              {formatMessage(a.email.sendingProgress, {
                sent: sendProgress.sent,
                total: sendProgress.total,
              })}
            </Typography>
          </Box>
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        justifyContent="flex-end"
        sx={{ mt: { xs: 2, sm: 3 }, animation: 'emSoftIn 0.45s ease both', animationDelay: '0.18s' }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            if (window.confirm(a.email.cancelSendConfirm)) {
              setSubject('');
              setHtmlContent('');
              setEmailList([]);
            }
          }}
          disabled={sending}
          sx={{
            maxWidth: { sm: 160 },
            borderRadius: 2.5,
            textTransform: 'none',
            fontWeight: 700,
            py: 1.1,
          }}
        >
          {t.common.cancel}
        </Button>
        <Button
          fullWidth
          variant="contained"
          startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
          onClick={handleSend}
          disabled={sending || !subject.trim() || !htmlContent.trim() || emailList.length === 0}
          sx={{
            maxWidth: { sm: 200 },
            borderRadius: 2.5,
            textTransform: 'none',
            fontWeight: 700,
            py: 1.1,
          }}
        >
          {sending ? a.email.sending : a.email.send}
        </Button>
      </Stack>
    </Container>
  );
};

export default EmailComposer;

